import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { applyBranding } from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PublicPage, PublicHeader, BrandMark, StatusCard } from "@/components/public/PublicShell";
import { Loader2, MailCheck, ArrowRight } from "lucide-react";

export default function RecruiterStart() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { applyBranding({ brand_primary: "#10B981" }); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/recruiter-onboard", { name, email, company });
      setSent(true);
      if (!data.sent) toast.message("Account ready. If the email doesn't arrive, use Sign in on the login page.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start your workspace");
    } finally { setBusy(false); }
  };

  return (
    <PublicPage className="flex flex-col">
      <PublicHeader sticky={false} right={<Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="recruiter-signin">Already have an account? <span className="font-semibold text-primary">Sign in</span></Link>}>
        <BrandMark />
      </PublicHeader>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        {!sent ? (
          <section className="w-full max-w-[480px] rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-primary">For employers</p>
              <h1 className="font-display text-[28px] font-semibold tracking-tight">Start hiring with AI</h1>
              <p className="text-sm text-muted-foreground leading-6">Enter your work email. We'll set up your hiring workspace and email you a one-click sign-in link, so there's no password to remember.</p>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="recruiter-start-form">
              <div className="space-y-2"><Label htmlFor="name">Your name</Label><Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" autoComplete="name" className="h-11" data-testid="recruiter-name" required /></div>
              <div className="space-y-2"><Label htmlFor="email">Work email</Label><Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" className="h-11" data-testid="recruiter-email" required /></div>
              <div className="space-y-2"><Label htmlFor="company">Company</Label><Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Inc." autoComplete="organization" className="h-11" data-testid="recruiter-company" /></div>
              <Button type="submit" size="lg" className="w-full h-11 font-semibold" disabled={busy} data-testid="recruiter-start-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Email my sign-in link <ArrowRight className="h-4 w-4 ml-1" /></>}</Button>
            </form>
          </section>
        ) : (
          <StatusCard icon={MailCheck} tone="primary" title="Check your inbox" testId="recruiter-sent"
            actions={<Button variant="outline" asChild><Link to="/login">Back to sign in</Link></Button>}>
            We emailed a one-click sign-in link to <strong className="text-foreground">{email}</strong>. It's valid for 30 minutes.
          </StatusCard>
        )}
      </main>
    </PublicPage>
  );
}
